import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { FaExclamationTriangle, FaCheckCircle, FaChevronDown, FaChevronRight } from 'react-icons/fa';
import { getBaseUrl } from './ApiContext';
import './FeatureHealth.css';

const baseUrl = getBaseUrl();

// Answers "is anything wrong" before it shows anything else, and collapses
// everything healthy into one line. On a normal day this page is almost
// empty, which is the point: the failure this whole feature guards against
// is somebody quietly stopping reading it, and a page that looks equally
// busy whether or not something is wrong becomes wallpaper in a fortnight.
//
// It holds no thresholds. Every verdict is decided in one file in
// lp_outreach and stored, so this page and the alert email cannot disagree.

const FINDING = ['cold', 'never', 'unreadable'];

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

function day(iso) {
    if (!iso) return null;
    const p = iso.slice(0, 10).split('-');
    if (p.length !== 3) return iso;
    return `${Number(p[2])} ${MONTHS[Number(p[1]) - 1]}`;
}

function ago(iso) {
    if (!iso) return null;
    const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
    if (mins < 2) return 'just now';
    if (mins < 60) return `${mins} minutes ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
    const days = Math.round(hrs / 24);
    return `${days} day${days === 1 ? '' : 's'} ago`;
}

// The question somebody would otherwise have to compose for themselves.
// Copying it is the whole action, because this page has no business trying
// to fix anything, and a button that pretended to would be worse than none.
function question(f) {
    return `The "${f.label}" feature looks like it has stopped working. `
        + `The health check says "${f.detail}". `
        + 'Can you look at the logs and tell me what happened, in plain words?';
}

// One sentence a person can act on. The module's own wording is kept
// below it rather than replaced, because that is the string somebody
// will search the code for.
function state(f) {
    const since = day(f.cold_since);
    if (f.verdict === 'never') return 'Has never produced anything.';
    if (f.verdict === 'unreadable') {
        return since ? `Could not be read since ${since}.` : 'Could not be read.';
    }
    const last = day(f.last_output);
    return [
        last ? `Last produced something on ${last}` : 'Has gone quiet',
        since ? `and has been quiet since ${since}` : null,
    ].filter(Boolean).join(' ') + '.';
}

function Finding({ f }) {
    const [copied, setCopied] = useState(false);
    const copy = () => {
        navigator.clipboard.writeText(question(f)).then(
            () => { setCopied(true); setTimeout(() => setCopied(false), 2500); },
            () => setCopied(false),
        );
    };
    return (
        <div className="fh-finding">
            <span className={`fh-dot fh-dot-${f.verdict}`} aria-hidden="true" />
            <div className="fh-finding-body">
                <div className="fh-finding-name">{f.label}</div>
                {f.note && <div className="fh-finding-what">{f.note}</div>}
                <div className="fh-finding-state">{state(f)}</div>
                <div className="fh-detail">{f.detail}</div>
            </div>
            <button type="button" className="fh-copy" onClick={copy}>
                {copied ? 'Copied' : 'Copy question'}
            </button>
        </div>
    );
}

export default function FeatureHealth() {
    const [data, setData] = useState(null);
    const [error, setError] = useState(false);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        axios.get(`${baseUrl}/api/feature-health`)
            .then((res) => { setData(res.data); setError(false); setLoading(false); })
            .catch(() => { setError(true); setLoading(false); });
    }, []);

    const [findings, warm] = useMemo(() => {
        if (!data || !data.features) return [[], []];
        return [
            data.features.filter((f) => FINDING.includes(f.verdict)),
            data.features.filter((f) => !FINDING.includes(f.verdict)),
        ];
    }, [data]);

    const title = (
        <h1>
            Feature health
            <span className="fh-wip">work in progress</span>
        </h1>
    );

    if (loading) return <div className="fh">{title}<p className="fh-quiet">Checking…</p></div>;

    if (error) {
        return (
            <div className="fh">
                {title}
                <p className="fh-warn">
                    The health check could not be reached. That is a problem with this page
                    rather than a verdict about anything else.
                </p>
            </div>
        );
    }

    // Never an empty table. A monitoring page that renders blank when it
    // cannot see anything reads as good news, which is the one thing it
    // must never do by accident.
    if (data.unreadable) {
        return <div className="fh">{title}<p className="fh-warn">{data.reason}</p></div>;
    }

    const total = data.features.length;
    const n = findings.length;

    return (
        <div className="fh">
            {title}
            <div className="fh-card">
                <div className="fh-verdict">
                    {n
                        ? <FaExclamationTriangle className="fh-verdict-icon fh-bad" aria-hidden="true" />
                        : <FaCheckCircle className="fh-verdict-icon fh-good" aria-hidden="true" />}
                    <div>
                        <div className={`fh-headline ${n ? 'fh-bad' : 'fh-good'}`}>
                            {n === 0
                                ? `All ${total} features are producing output as usual`
                                : `${n} thing${n === 1 ? '' : 's'} need${n === 1 ? 's' : ''} a look`}
                        </div>
                        <div className="fh-quiet">
                            {n > 0 ? `${total - n} of ${total} are fine. ` : ''}
                            {data.checked_at
                                ? `Checked ${ago(data.checked_at)}, and this shows what that check found rather than what is true this second.`
                                : 'This has not been checked yet.'}
                        </div>
                    </div>
                </div>

                {findings.map((f) => <Finding key={f.key} f={f} />)}

                {warm.length > 0 && (
                    <div>
                        <button
                            type="button"
                            className="fh-toggle"
                            aria-expanded={open}
                            onClick={() => setOpen(!open)}
                        >
                            {open
                                ? <FaChevronDown aria-hidden="true" />
                                : <FaChevronRight aria-hidden="true" />}
                            {warm.length} feature{warm.length === 1 ? '' : 's'} producing normally
                        </button>
                        {open && (
                            <div className="fh-warm">
                                {warm.map((f) => (
                                    <div className="fh-warm-row" key={f.key}>
                                        <span className="fh-dot fh-dot-ok" aria-hidden="true" />
                                        <div className="fh-warm-name">
                                            {f.label}
                                            {f.note && <div className="fh-quiet">{f.note}</div>}
                                        </div>
                                        <span className="fh-when">
                                            {f.last_output ? day(f.last_output) : 'never'}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            <p className="fh-foot">
                Each feature is measured against its own history, so quiet means quieter than
                that feature has ever been rather than simply quiet. A tool that only fires
                some weeks does not appear here for being idle this week.
            </p>
        </div>
    );
}
